import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { countries } from "country-data-list";
import { Globe2, List } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ComposableMap,
  Geographies,
  Geography,
} from "react-simple-maps";

export interface SalesByCountryData {
  country_code: string;
  country_name: string;
  flag_emoji: string;
  count: number;
  amount: number;
  amount_formatted: string;
  percentage: number;
  count_percentage: number;
  average_order_value: number;
  average_order_value_formatted: string;
  min_order_value: number;
  min_order_value_formatted: string;
  max_order_value: number;
  max_order_value_formatted: string;
}

interface Props {
  salesByCountry: SalesByCountryData[];
  isLoading?: boolean;
}

const geoUrl = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

export default function SalesByCountryCard({ salesByCountry = [], isLoading }: Props) {
  const { t } = useTranslation("dashboard");
  const [activeTab, setActiveTab] = useState("map");
  const [activeCountry, setActiveCountry] = useState<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [mapScale, setMapScale] = useState(130);
  const maxAmount = Math.max(...salesByCountry.map((sale) => sale.amount), 1);
  const sortedSales = [...salesByCountry].sort((a, b) => b.amount - a.amount);

  const updateMapScale = useCallback(() => {
    const width = mapContainerRef.current?.offsetWidth ?? 500;
    if (width < 400) setMapScale(90);
    else if (width < 600) setMapScale(110);
    else setMapScale(130);
  }, []);

  useEffect(() => {
    updateMapScale();
    window.addEventListener("resize", updateMapScale);
    return () => window.removeEventListener("resize", updateMapScale);
  }, [updateMapScale]);

  const mapNameToAlpha2 = useMemo(() => {
    const map: Record<string, string> = {};
    countries.all.forEach((c: any) => {
      if (c.alpha2 && c.name) map[c.name] = c.alpha2;
    });
    map["United States of America"] = "US";
    return map;
  }, []);

  const alpha3ToAlpha2 = useMemo(() => {
    const map: Record<string, string> = {};
    countries.all.forEach((c: any) => {
      if (c.alpha3 && c.alpha2) map[c.alpha3] = c.alpha2;
    });
    return map;
  }, []);

  const countriesWithSales = useMemo(() => {
    return new Set(
      salesByCountry.map((s) => alpha3ToAlpha2[s.country_code] || s.country_code)
    );
  }, [salesByCountry, alpha3ToAlpha2]);

  const getCountryColor = (geo: any) => {
    const code = mapNameToAlpha2[geo.properties.name];
    return countriesWithSales.has(code || "") ? "#A9D342" : "#27272A";
  };

  const getTooltipContent = (geo: any) => {
    if (!geo) return null;
    const code = mapNameToAlpha2[geo.properties.name];
    const sales = salesByCountry.find(
      (sale) =>
        (alpha3ToAlpha2[sale.country_code] || sale.country_code) === code
    );

    if (!sales) return null;

    return (
      <div className="bg-zinc-950/95 md:bg-zinc-900/90 md:backdrop-blur-sm p-3 rounded-md min-w-[180px] border border-zinc-800">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg">{sales.flag_emoji}</span>
          <span className="text-white font-medium text-sm">
            {sales.country_name}
          </span>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-400">{t("paid")}</span>
            <span className="text-white font-medium">{sales.amount_formatted}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-zinc-400">{t("salesCount", { count: sales.count })}</span>
            <span className="text-zinc-400">{sales.percentage.toFixed(1)}%</span>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className="border border-zinc-800 rounded-lg shadow-lg bg-zinc-950 p-4 h-full min-h-[320px]">
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <Globe2 className="w-8 h-8 text-zinc-600 animate-pulse" />
          <span className="text-sm text-zinc-500 animate-pulse">{t("loading")}</span>
        </div>
      </Card>
    );
  }

  if (!salesByCountry.length) {
    return (
      <Card className="border border-zinc-800 rounded-lg shadow-lg bg-zinc-950 p-4 h-full min-h-[320px]">
        <div className="flex flex-col items-center justify-center h-full gap-3">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-zinc-800/60">
            <Globe2 className="w-6 h-6 text-zinc-500" />
          </div>
          <p className="text-sm text-zinc-400 text-center max-w-[200px]">
            {t("noSalesData")}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border border-zinc-800 rounded-lg shadow-lg bg-zinc-950 h-full">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-zinc-800">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-white font-semibold text-base sm:text-lg truncate">
              {t("common:sell")}
            </span>
            <div className="rounded bg-zinc-900 px-2 py-1 flex items-center justify-center shrink-0">
              <span className="text-xs text-zinc-400">{t("byCountry")}</span>
            </div>
          </div>

          <TabsList className="grid grid-cols-2 h-9 bg-zinc-900 rounded-lg gap-2">
            <TabsTrigger
              value="map"
              className="group h-7 w-7 p-0 rounded-md"
              aria-label={t("mapView")}
            >
              <Globe2 className="w-4 h-4 text-zinc-400 group-data-[state=active]:text-white" />
            </TabsTrigger>
            <TabsTrigger
              value="list"
              className="group h-7 w-7 p-0 rounded-md"
              aria-label={t("listView")}
            >
              <List className="w-4 h-4 text-zinc-400 group-data-[state=active]:text-white" />
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="map" className="mt-0 flex-1 min-h-0">
          <div
            ref={mapContainerRef}
            className="relative h-full min-h-[250px] sm:min-h-[280px] w-full touch-pan-y md:touch-auto"
            style={{ willChange: activeTab === "map" ? "transform" : undefined }}
          >
            {activeTab === "map" && (
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{ scale: mapScale * 1.2, center: [0, 20] }}
                className="w-full h-full max-md:pointer-events-none"
              >
                <Geographies geography={geoUrl}>
                  {({ geographies }) =>
                    geographies.map((geo, index) => (
                      <Geography
                        key={geo.rsmKey + index}
                        geography={geo}
                        fill={getCountryColor(geo)}
                        stroke="#374151"
                        strokeWidth={0.5}
                        onMouseEnter={() => setActiveCountry(geo)}
                        onMouseLeave={() => setActiveCountry(null)}
                        data-tooltip-id="country-tooltip"
                        style={{
                          default: { outline: "none" },
                          hover: { outline: "none", cursor: "pointer", fill: "#8AB835" },
                          pressed: { outline: "none" },
                        }}
                      />
                    ))
                  }
                </Geographies>
              </ComposableMap>
            )}

            {activeCountry && (
              <div className="absolute top-2 left-2 sm:top-4 sm:left-4 z-10 max-w-[calc(100%-16px)] hidden md:block">
                {getTooltipContent(activeCountry)}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-0 flex-1 min-h-0 overflow-auto">
          <div className="p-3 sm:p-4 space-y-2 sm:space-y-3 min-h-[250px] sm:min-h-[280px] max-h-[350px] sm:max-h-[400px] overflow-y-auto">
            {sortedSales.map((sale, index) => (
              <div key={sale.country_code + index} className="relative group">
                <div
                  className="absolute left-0 top-0 h-full bg-zinc-900 rounded-lg transition-all duration-300"
                  style={{ width: `${(sale.amount / maxAmount) * 100}%` }}
                />
                <div className="relative flex items-center justify-between p-3 sm:p-4 rounded-lg bg-zinc-900/50 z-10">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <span className="text-base sm:text-lg shrink-0">{sale.flag_emoji}</span>
                    <span className="text-white text-xs sm:text-sm font-medium truncate">
                      {sale.country_name}
                    </span>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <div className="text-white text-sm sm:text-base font-medium">
                      {t("salesCount", { count: sale.count })}
                    </div>
                    <div className="text-zinc-400 text-xs">
                      {sale.amount_formatted}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </Card>
  );
}
